/**
 * 数据契约：ExperimentPack。
 * 对应技术设计 §11.1（顶层结构）与 §11.3（最小氧气实验包示例）。
 *
 * 这份定义是 S0「契约冻结」的前端侧起点（§21.2）：将来前后端共用一份定义生成类型与校验器，
 * 所以此处的枚举必须与文档逐字一致，不做「简化版」。
 */

import type { Pose } from "../eom/lab-object.js";
import type { TargetGraph } from "../eom/assembly-graph.js";

export const EXPERIMENT_PACK_SCHEMA_VERSION = "chem.experiment.v1";

/** 运行模式（§4.1）。learn 显示连接点与目标虚影，exam 隐藏引导只记录结算。 */
export type LabMode = "learn" | "practice" | "exam";

export const LAB_MODES: readonly LabMode[] = ["learn", "practice", "exam"] as const;

/**
 * 实验类型。**9 类，与技术设计 §11.1 完全一致**。
 * 其中 substance_property 是 v1.3 依据 002 并集口径补入的（铁丝燃烧、氢气验纯与燃烧、CO₂ 性质）。
 */
export type ChemExperimentType =
  | "gas_preparation"
  | "closed_combustion"
  | "substance_property"
  | "separation_purification"
  | "solution_preparation"
  | "electrolysis"
  | "acid_base_salt"
  | "metal_activity"
  | "basic_operation";

export const CHEM_EXPERIMENT_TYPES: readonly ChemExperimentType[] = [
  "gas_preparation",
  "closed_combustion",
  "substance_property",
  "separation_purification",
  "solution_preparation",
  "electrolysis",
  "acid_base_salt",
  "metal_activity",
  "basic_operation",
] as const;

/** 难度（§11.1 / §16.0 映射规则：基础→basic，中档→middle，压轴难点→challenge） */
export type ExperimentDifficulty = "basic" | "middle" | "challenge";

export const EXPERIMENT_DIFFICULTIES: readonly ExperimentDifficulty[] = [
  "basic",
  "middle",
  "challenge",
] as const;

/** 流程模板（§16.2 九种模板，与 ChemExperimentType 同名但语义不同：一个是分类，一个是步骤骨架） */
export type FlowTemplate = string;

export type ExamPointKind =
  | "apparatus"
  | "operation_order"
  | "safety"
  | "phenomenon"
  | "error_analysis"
  | "variable_control"
  | "conclusion";

export const EXAM_POINT_KINDS: readonly ExamPointKind[] = [
  "apparatus",
  "operation_order",
  "safety",
  "phenomenon",
  "error_analysis",
  "variable_control",
  "conclusion",
] as const;

export interface ExamPointSpec {
  id: string;
  title: string;
  kind: ExamPointKind;
  /** 由哪些 LabEvent / state / relation 证明（§22 红线 10 要求可回溯） */
  evidence: string[];
}

export interface AssetRef {
  ref: string;
  kind: "equipment" | "reagent" | "phenomenon" | "ui" | "sound";
  required: boolean;
}

export interface SceneSpec {
  layout: string;
  zones: string[];
}

/**
 * 实验包里的一件器材。§11.3 里最少只写 `{ id, type }`，其余由 EquipmentRegistry 的模板补齐；
 * 实验包可以覆盖 name / pose / state / zone。
 */
export interface EquipmentSpec {
  id: string;
  type: string;
  name?: string;
  /** 试剂类器材指向的物质 id，如 "kmno4"（§11.3） */
  substanceId?: string;
  pose?: Partial<Pose>;
  state?: Record<string, unknown>;
  /** 初始所在区域，缺省由 LayoutEngine 决定（§4.2） */
  zone?: string;
}

/** 被 ref 指向的外部文件。P0 只负责取回并挂上，内容由 P2/P3/P4 消费。 */
export interface ExternalRef {
  ref: string;
  /** loader 展开 ref 后填入的原始 JSON；未展开时为 undefined。 */
  resolved?: unknown;
}

export type MissionSpec = ExternalRef;
export type RuleSpec = ExternalRef;
export type KnowledgeSpec = ExternalRef;
export type AnimationSpec = ExternalRef;
export type AssessmentSpec = ExternalRef;

export interface ExperimentPack {
  schemaVersion: typeof EXPERIMENT_PACK_SCHEMA_VERSION;
  id: string;
  title: string;
  subject: "chemistry";
  gradeRange: string[];
  experimentType: ChemExperimentType;
  difficulty: ExperimentDifficulty;
  flowTemplate: FlowTemplate;
  examPoints: ExamPointSpec[];
  assetRefs: AssetRef[];
  scene: SceneSpec;
  equipment: EquipmentSpec[];
  mission: MissionSpec;
  rules: RuleSpec[];
  knowledge: KnowledgeSpec[];
  animations: AnimationSpec[];
  /**
   * §11.1 声明了本字段，但 §11.3 的示例包没有它，且消费方 Assessment Engine 在 P4 才落地。
   * 因此本实现按可选处理：给了就校验，没给不拦。
   */
  assessment?: AssessmentSpec;
  /** Mission 目标装配图（§11.2），P2 消费。 */
  target?: TargetGraph;
}
